import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { matches } from "@/mocks/matches";
import { teams, teamMap } from "@/mocks/teams";
import type { Match } from "@/types";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TeamLogo from "@/components/base/TeamLogo";
import StatusBadge from "@/components/base/StatusBadge";
import ScheduleCalendar from "@/pages/schedule/components/ScheduleCalendar";
import { formatDateKey, formatTime, formatDateLabel, weekdayLabel, todayKey, buildDateRange } from "@/utils/format";

const PAGE_SIZE = 8;

export default function Schedule() {
  const [selectedKey, setSelectedKey] = useState(todayKey());
  const [teamId, setTeamId] = useState("all");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const matchDates = useMemo(() => new Set(matches.map((m) => formatDateKey(m.startTime))), []);
  const quickDates = useMemo(() => buildDateRange(todayKey(), 6), []);
  const showingAll = teamId !== "all";

  const teamMatches = useMemo(
    () =>
      matches
        .filter((m) => m.homeTeamId === teamId || m.awayTeamId === teamId)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [teamId],
  );

  const dayMatches = useMemo(
    () =>
      matches
        .filter((m) => formatDateKey(m.startTime) === selectedKey)
        .filter((m) => teamId === "all" || m.homeTeamId === teamId || m.awayTeamId === teamId)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [selectedKey, teamId],
  );

  const list: Match[] = showingAll ? teamMatches.slice(0, visible) : dayMatches;
  const canLoadMore = showingAll && visible < teamMatches.length;

  return (
    <div className="px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "赛程与结果" }]} />

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">NBA赛程与结果</h1>
        <p className="mt-1.5 text-sm text-foreground-500">按日期查看完整赛程与赛果，或按球队筛选该队全部比赛</p>
      </header>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
        <aside className="flex flex-col gap-4">
          <ScheduleCalendar selectedKey={selectedKey} onSelect={setSelectedKey} matchDates={matchDates} />

          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h2 className="text-sm font-semibold text-foreground-900">按球队筛选</h2>
            <select
              value={teamId}
              onChange={(e) => { setTeamId(e.target.value); setVisible(PAGE_SIZE); }}
              className="mt-3 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400"
            >
              <option value="all">全部球队</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.city}{t.name}</option>
              ))}
            </select>
            {showingAll && (
              <button
                type="button"
                onClick={() => { setTeamId("all"); setVisible(PAGE_SIZE); }}
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
              >
                <i className="ri-refresh-line" /> 恢复按日期查看
              </button>
            )}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h2 className="text-sm font-semibold text-foreground-900">快速选择日期</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {quickDates.map((key) => {
                const isActive = key === selectedKey && !showingAll;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { setSelectedKey(key); setTeamId("all"); }}
                    className={`rounded-md px-2.5 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${
                      isActive ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {weekdayLabel(`${key}T00:00:00`)} {formatDateLabel(`${key}T00:00:00`)}
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        <section className="rounded-lg border border-background-200 bg-background-50">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-background-200 px-4 py-3">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              {showingAll ? `${teamMap[teamId]?.city ?? ""}${teamMap[teamId]?.name ?? ""} 全部赛程` : `${selectedKey} 赛程`}
            </h2>
            <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-900">
              {showingAll ? teamMatches.length : dayMatches.length} 场比赛
            </span>
          </div>

          {list.length > 0 ? (
            <>
              <ul className="divide-y divide-background-100">
                {list.map((match) => {
                  const home = teamMap[match.homeTeamId];
                  const away = teamMap[match.awayTeamId];
                  const finished = match.status !== "upcoming";
                  return (
                    <li key={match.id}>
                      <Link to={`/match/${match.id}`} className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-background-100">
                        <div className="w-16 shrink-0 text-center">
                          <p className="font-heading text-base font-bold text-primary-600">{formatTime(match.startTime)}</p>
                          <p className="text-[11px] text-foreground-500">{formatDateKey(match.startTime).slice(5)}</p>
                        </div>

                        <div className="flex flex-1 items-center justify-center gap-2 min-w-0">
                          <span className="flex-1 truncate text-right text-sm font-medium text-foreground-800">{home?.name}</span>
                          {home && <TeamLogo team={home} size="sm" />}
                          {finished ? (
                            <span className="font-heading text-base font-bold text-foreground-950 whitespace-nowrap">
                              {match.homeScore} : {match.awayScore}
                            </span>
                          ) : (
                            <span className="text-xs font-semibold text-foreground-400">VS</span>
                          )}
                          {away && <TeamLogo team={away} size="sm" />}
                          <span className="flex-1 truncate text-sm font-medium text-foreground-800">{away?.name}</span>
                        </div>

                        <div className="hidden w-24 shrink-0 justify-end sm:flex">
                          <StatusBadge status={match.status} />
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {canLoadMore && (
                <div className="flex justify-center border-t border-background-100 py-4">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 rounded-md border border-primary-300 bg-background-50 px-5 py-2 text-sm font-semibold text-primary-600 whitespace-nowrap transition-colors hover:bg-primary-50"
                  >
                    <i className="ri-add-line" /> 加载更多（剩余 {teamMatches.length - visible} 场）
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center px-6 py-16 text-center">
              <i className="ri-calendar-close-line text-4xl text-foreground-300" />
              <p className="mt-3 text-sm font-medium text-foreground-700">
                {showingAll ? "该球队暂无赛程安排" : `${selectedKey} 当天没有赛事`}
              </p>
              <p className="mt-1 text-xs text-foreground-500">可以选择其他日期，或前往直播赛事页浏览正在进行的比赛</p>
              <Link
                to="/live"
                className="mt-4 inline-flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
              >
                <i className="ri-live-line" /> 前往直播赛事
              </Link>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}